import clsx from "clsx";
import { TransitionGroup } from "react-transition-group";
// MUI
import { Box, Collapse } from "@mui/material";
// Hooks
import { useContext, useEffect, useMemo, useState } from "react";
import { useNotificationList } from "@/Common/hooks/Notifications";
import { useIntersection } from "@/Common/hooks/Layout/useIntersection";
// Components
import { NotificationListFooter } from "@/Layouts/views/Header/components/Notifications";
import { NotificationItem } from "@/Dashboard/components/Notifications";
import { NotificationsLoader } from "@/Dashboard/components/DashboardLoader";
import { INotification } from "@/Common/api/types/notifications";
import { ListRefContext } from "@/Common/context/notifications/ListRefContext";

interface INotificationListProps {
	onlyUnRead: boolean;
	archived: boolean;
}

export const NotificationList = ({ onlyUnRead, archived }: Readonly<INotificationListProps>) => {
	const { notifications, isLoading, hasNextPage, fetchNextPage } = useNotificationList({
		enabled: true,
		showOnlyUnread: onlyUnRead,
		archived,
	});
	const listId = `notification-list-${archived ? "archived" : "inbox"}`;

	const [selected, setSelected] = useState("");
	const [archivedItems, setArchivedItems] = useState<string[]>([]);
	const { observedElementRef, wrapperContainerRef } = useIntersection({
		rootMargin: "0px 0px 200px 0px",
		onScrolledIntoView() {
			if (!isLoading && hasNextPage) {
				fetchNextPage();
			}
		},
	});
	const listRefs = useContext(ListRefContext);

	const onItemArchivedDone = ({ _id }: INotification) => {
		setArchivedItems((prev) => [...prev, _id]);
	};

	const onItemArchivedFail = ({ _id }: INotification) => {
		setArchivedItems((prev) => prev.filter((id) => id !== _id));
	};

	useEffect(() => {
		const domElement = document.getElementById(listId);
		const ref = archived ? listRefs?.archiveListRef : listRefs?.inboxListRef;
		if (ref && domElement) {
			ref.current = domElement;
		}
	}, [listRefs, listId]);

	const MemoizedList = useMemo(() => {
		const remainingNotifications = notifications.filter((notification) => !archivedItems.includes(notification._id));
		return (
			<Box
				id={listId}
				ref={wrapperContainerRef}
				className={clsx("max-h-[400px]", {
					"pr-[10px] overflow-y-scroll overflow-x-hidden": remainingNotifications.length > 5,
				})}
			>
				<TransitionGroup>
					{remainingNotifications.map((notification, index) => (
						<Collapse key={notification._id}>
							<NotificationItem
								notification={notification}
								lastItem={index === remainingNotifications.length - 1}
								selected={selected}
								setSelected={setSelected}
								optimisticArchiveHandlers={{ onDone: onItemArchivedDone, onFail: onItemArchivedFail }}
							/>
						</Collapse>
					))}
				</TransitionGroup>
				<span ref={observedElementRef} className="w-0 h-0"></span>
				{hasNextPage && <NotificationsLoader />}
			</Box>
		);
	}, [notifications, archivedItems, hasNextPage, selected, wrapperContainerRef, observedElementRef]);

	if (isLoading) {
		return <NotificationsLoader />;
	}

	if (!isLoading && !notifications.length) {
		return <NotificationListFooter hasItems={false} />;
	}

	return MemoizedList;
};
